import { useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Modal } from "../../../components/Modal";
import { Card } from "../../../components/Card";
import { Button } from "../../../components/Button";
import { Form } from "../../../components/Form";
import { FormActions } from "../../../components/FormActions";
import { FormGroup } from "../../../components/FormGroup";
import { FormLabel } from "../../../components/FormLabel";
import { Input } from "../../../components/Input";
import { PackageCard } from "../../../components/PackageCard";
import { InfoBox } from "../../../components/InfoBox";
import { PaymentTermsSection } from "./PaymentTermsSection";
import { spacing } from "@styling/sizing";
import colors from "@styling/colors";
import { BillingItemType } from "@KassaroAB/shared/types/enums/BillingItemType";
import { BillingPeriod } from "@KassaroAB/shared/types/enums/BillingPeriod";
import type { RestBillingItem } from "@KassaroAB/shared/types/billingItem";
import type { AvailableBillingItemId, StoreId } from "@KassaroAB/shared/types/ids";
import {
  useSubscribeBillingItemMutation,
} from "../../../query/billingItemsQuery";
import { useGetStoreAvailableBillingItemsQuery } from "../../../query/availableBillingItemsQuery";
import { toDateString } from "../../../utils/dateUtils";
import { getQuantityOnDate } from "../../../utils/billingItemSegments";
import { useIsAdmin } from "../../../hooks/useIsAdmin";

const SectionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x4}px;
`;

const ItemGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: ${spacing.x2}px;
`;

const SelectedList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
`;

const SelectedRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: ${spacing.x2}px;
  flex-wrap: wrap;
`;

const SelectedName = styled.div`
  font-weight: 600;
  color: ${colors.text};
  flex: 1;
  min-width: 140px;
`;

const QuantityInput = styled(Input)`
  width: 120px;
`;

const EmptyText = styled.p`
  margin: 0;
  color: ${colors.textMuted};
`;

interface ManagePaymentTerminalsModalProps {
  isOpen: boolean;
  onClose: () => void;
  billingItems: RestBillingItem[] | undefined;
  defaultBillingPeriod?: BillingPeriod;
  defaultStartDate?: string;
}

export const ManagePaymentTerminalsModal = ({
  isOpen,
  onClose,
  billingItems,
  defaultBillingPeriod = BillingPeriod.MONTHLY,
  defaultStartDate,
}: ManagePaymentTerminalsModalProps) => {
  if (!isOpen) {
    return null;
  }

  return (
    <ManagePaymentTerminalsModalContent
      onClose={onClose}
      billingItems={billingItems}
      defaultBillingPeriod={defaultBillingPeriod}
      defaultStartDate={defaultStartDate}
    />
  );
};

const ManagePaymentTerminalsModalContent = ({
  onClose,
  billingItems,
  defaultBillingPeriod = BillingPeriod.MONTHLY,
  defaultStartDate,
}: Omit<ManagePaymentTerminalsModalProps, "isOpen">) => {
  const { t } = useTranslation();
  const { storeId } = useParams<{ storeId: StoreId }>();
  const isAdmin = useIsAdmin();
  const today = toDateString(new Date());
  const [selectedIds, setSelectedIds] = useState<AvailableBillingItemId[]>([]);
  const [quantities, setQuantities] = useState<
    Partial<Record<AvailableBillingItemId, number>>
  >({});
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>(
    defaultBillingPeriod
  );
  const [effectiveFrom, setEffectiveFrom] = useState(
    defaultStartDate ?? today
  );
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data: availableBillingItems } = useGetStoreAvailableBillingItemsQuery(
    storeId ?? null
  );
  const { mutateAsync: subscribe, isPending } =
    useSubscribeBillingItemMutation(storeId ?? null);

  const paymentMethods = (availableBillingItems ?? []).filter(
    (item) => item.type === BillingItemType.PAYMENT_METHOD
  );
  const activeIds = (billingItems ?? [])
    .filter(
      (billingItem) =>
        billingItem.type === BillingItemType.PAYMENT_METHOD &&
        getQuantityOnDate(
          [billingItem],
          billingItem.availableBillingItemId,
          today
        ) > 0
    )
    .map((billingItem) => billingItem.availableBillingItemId);

  const toAdd = selectedIds.filter((id) => !activeIds.includes(id));
  const hasChanges = toAdd.length > 0;

  const toggleItem = (itemId: AvailableBillingItemId) => {
    if (activeIds.includes(itemId)) {
      return;
    }
    const isSelected = selectedIds.includes(itemId);
    const nextSelectedIds = isSelected
      ? selectedIds.filter((id) => id !== itemId)
      : [...selectedIds, itemId];
    setSelectedIds(nextSelectedIds);
    setQuantities((prev) => {
      if (isSelected) {
        const next = { ...prev };
        delete next[itemId];
        return next;
      }
      return { ...prev, [itemId]: prev[itemId] ?? 1 };
    });
  };

  const handleClose = () => {
    setSubmitError(null);
    onClose();
  };

  const handleSubmit = async () => {
    setSubmitError(null);

    const addResults = await Promise.allSettled(
      toAdd.map((availableBillingItemId) =>
        subscribe({
          availableBillingItemId,
          quantity: Math.max(1, quantities[availableBillingItemId] ?? 1),
          startDate: effectiveFrom,
          billingPeriod,
        })
          .then(() => availableBillingItemId)
          .catch((error: Error) =>
            Promise.reject({ availableBillingItemId, error })
          )
      )
    );

    const rejected = addResults.filter(
      (result): result is PromiseRejectedResult => result.status === "rejected"
    );

    if (rejected.length > 0) {
      const details = rejected.map((result) => {
        const reason = result.reason as {
          availableBillingItemId: AvailableBillingItemId;
          error: Error;
        };
        return `${reason.availableBillingItemId}: ${reason.error.message}`;
      });
      setSubmitError(details.join("; "));
      return;
    }

    handleClose();
  };

  return (
    <Modal
      isOpen
      onClose={handleClose}
      title={t("packageSelection.addTerminals")}
      maxWidth={900}
    >
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          void handleSubmit();
        }}
      >
        <SectionsContainer>
          <PaymentTermsSection
            paymentTerms={billingPeriod}
            onPaymentTermsChange={setBillingPeriod}
            startDate={effectiveFrom}
            onStartDateChange={setEffectiveFrom}
            minStartDate={isAdmin ? undefined : today}
          />

          {paymentMethods.length === 0 ? (
            <EmptyText>{t("billingItems.noOtherItemsAvailable")}</EmptyText>
          ) : (
            <FormGroup>
              <ItemGrid>
                {paymentMethods.map((item) => (
                  <PackageCard
                    key={item.id}
                    id="availableBillingItem"
                    name={item.name}
                    description={item.description}
                    price={item.pricing}
                    selected={
                      activeIds.includes(item.id) ||
                      selectedIds.includes(item.id)
                    }
                    onSelect={() => toggleItem(item.id)}
                    selectionType="checkbox"
                    disabled={activeIds.includes(item.id)}
                    comingSoon={item.comingSoon ?? false}
                  />
                ))}
              </ItemGrid>
            </FormGroup>
          )}

          {toAdd.length > 0 && (
            <FormGroup>
              <FormLabel>{t("packageSelection.quantity")}</FormLabel>
              <SelectedList>
                {toAdd.map((itemId) => {
                  const item = paymentMethods.find(
                    (paymentMethod) => paymentMethod.id === itemId
                  );
                  return (
                    <SelectedRow key={itemId}>
                      <SelectedName>
                        {item?.name ?? itemId}
                      </SelectedName>
                      <QuantityInput
                        type="number"
                        min="1"
                        value={quantities[itemId] ?? 1}
                        onChange={(e) => {
                          const parsed = parseInt(e.target.value, 10);
                          setQuantities((prev) => ({
                            ...prev,
                            [itemId]: isNaN(parsed)
                              ? 1
                              : Math.max(1, parsed),
                          }));
                        }}
                      />
                    </SelectedRow>
                  );
                })}
              </SelectedList>
            </FormGroup>
          )}

          <Card>
            {submitError && (
              <InfoBox variant="error">
                {t("packageSelectionModal.saveFailed", {
                  message: submitError,
                })}
              </InfoBox>
            )}
            <FormActions>
              <Button
                type="button"
                $color="secondary"
                onClick={handleClose}
                disabled={isPending}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={isPending || !hasChanges}>
                {isPending
                  ? t("packageSelectionModal.saving")
                  : t("common.saveChanges")}
              </Button>
            </FormActions>
          </Card>
        </SectionsContainer>
      </Form>
    </Modal>
  );
};
